import Vue from 'vue';
import checklistAPI from '@/api/checklists';

export default {
  namespaced: true,

  state: () => ({
    byTask: {},
    loadingByTask: {},
  }),

  getters: {
    forTask: (state) => (taskUuid) => state.byTask[taskUuid] ?? [],
    isLoading: (state) => (taskUuid) => Boolean(state.loadingByTask[taskUuid]),
    remainingForTask: (state) => (taskUuid) =>
      (state.byTask[taskUuid] ?? []).reduce(
        (total, checklist) => total + (checklist.items ?? []).filter((item) => !item.is_checked).length,
        0
      ),
  },

  mutations: {
    SET_CHECKLISTS(state, { taskUuid, checklists }) {
      Vue.set(state.byTask, taskUuid, checklists);
    },
    SET_LOADING(state, { taskUuid, loading }) {
      Vue.set(state.loadingByTask, taskUuid, loading);
    },
    ADD_CHECKLIST(state, { taskUuid, checklist }) {
      Vue.set(state.byTask, taskUuid, [...(state.byTask[taskUuid] ?? []), checklist]);
    },
    UPDATE_CHECKLIST(state, { taskUuid, checklist }) {
      Vue.set(
        state.byTask,
        taskUuid,
        (state.byTask[taskUuid] ?? []).map((existing) =>
          existing.uuid === checklist.uuid ? { ...existing, ...checklist } : existing
        )
      );
    },
    DELETE_CHECKLIST(state, { taskUuid, checklistUuid }) {
      Vue.set(
        state.byTask,
        taskUuid,
        (state.byTask[taskUuid] ?? []).filter((checklist) => checklist.uuid !== checklistUuid)
      );
    },
    ADD_ITEM(state, { taskUuid, checklistUuid, item }) {
      Vue.set(
        state.byTask,
        taskUuid,
        (state.byTask[taskUuid] ?? []).map((checklist) =>
          checklist.uuid === checklistUuid ? { ...checklist, items: [...(checklist.items ?? []), item] } : checklist
        )
      );
    },
    UPDATE_ITEM(state, { taskUuid, checklistUuid, item }) {
      Vue.set(
        state.byTask,
        taskUuid,
        (state.byTask[taskUuid] ?? []).map((checklist) =>
          checklist.uuid === checklistUuid
            ? {
                ...checklist,
                items: (checklist.items ?? []).map((existing) => (existing.uuid === item.uuid ? item : existing)),
              }
            : checklist
        )
      );
    },
    DELETE_ITEM(state, { taskUuid, checklistUuid, itemUuid }) {
      Vue.set(
        state.byTask,
        taskUuid,
        (state.byTask[taskUuid] ?? []).map((checklist) =>
          checklist.uuid === checklistUuid
            ? { ...checklist, items: (checklist.items ?? []).filter((item) => item.uuid !== itemUuid) }
            : checklist
        )
      );
    },
  },

  actions: {
    async fetchForTask({ commit }, { taskUuid, workflowUuid = null, silent = false }) {
      if (!silent) {
        commit('SET_LOADING', { taskUuid, loading: true });
      }
      try {
        const response = workflowUuid
          ? await checklistAPI.getForWorkflowTask(workflowUuid, taskUuid)
          : await checklistAPI.getForTask(taskUuid);
        commit('SET_CHECKLISTS', { taskUuid, checklists: response.data.data });
      } finally {
        if (!silent) {
          commit('SET_LOADING', { taskUuid, loading: false });
        }
      }
    },

    async createChecklist({ commit }, { taskUuid, workflowUuid = null, name }) {
      const response = workflowUuid
        ? await checklistAPI.createForWorkflowTask(workflowUuid, taskUuid, { name })
        : await checklistAPI.createForTask(taskUuid, { name });
      commit('ADD_CHECKLIST', { taskUuid, checklist: response.data.data });
      return response.data.data;
    },

    async renameChecklist({ commit }, { taskUuid, checklistUuid, name }) {
      const response = await checklistAPI.updateChecklist(checklistUuid, { name });
      commit('UPDATE_CHECKLIST', { taskUuid, checklist: response.data.data });
    },

    async deleteChecklist({ commit }, { taskUuid, checklistUuid }) {
      await checklistAPI.deleteChecklist(checklistUuid);
      commit('DELETE_CHECKLIST', { taskUuid, checklistUuid });
    },

    async addItem({ commit }, { taskUuid, checklistUuid, name }) {
      const response = await checklistAPI.createItem(checklistUuid, { name });
      commit('ADD_ITEM', { taskUuid, checklistUuid, item: response.data.data });
      return response.data.data;
    },

    async updateItem({ commit }, { taskUuid, checklistUuid, itemUuid, data }) {
      const response = await checklistAPI.updateItem(itemUuid, data);
      commit('UPDATE_ITEM', { taskUuid, checklistUuid, item: response.data.data });
      return response.data.data;
    },

    async deleteItem({ commit }, { taskUuid, checklistUuid, itemUuid }) {
      await checklistAPI.deleteItem(itemUuid);
      commit('DELETE_ITEM', { taskUuid, checklistUuid, itemUuid });
    },
  },
};
